import { Pencil, Trash2 } from 'lucide-react';
import { FormattedDate } from 'react-intl';

import { useIntl } from '../../hooks/useIntl.js';
import type { AppUser } from '../../types/user';
import { Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui';
import { TagBadge } from './TagBadge';

interface UsersTableProps {
  users: AppUser[];
  onEdit: (user: AppUser) => void;
  onDelete: (user: AppUser) => void;
  currentUserId?: string;
}

export function UsersTable({ users, onEdit, onDelete, currentUserId }: UsersTableProps) {
  const intl = useIntl();

  if (!users.length) {
    return (
      <div className="text-sm text-foreground">
        {intl.formatMessage({ id: 'users.table.noUsers' })}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader className="bg-background-light">
        <TableRow>
          <TableHead>{intl.formatMessage({ id: 'users.table.username' })}</TableHead>
          <TableHead className="w-32">{intl.formatMessage({ id: 'users.table.role' })}</TableHead>
          <TableHead className="w-48">{intl.formatMessage({ id: 'users.table.tags' })}</TableHead>
          <TableHead className="w-64">
            {intl.formatMessage({ id: 'users.table.created' })}
          </TableHead>
          <TableHead className="w-64">
            {intl.formatMessage({ id: 'users.table.updated' })}
          </TableHead>
          <TableHead className="w-28 text-right">
            {intl.formatMessage({ id: 'users.table.actions' })}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => {
          const isCurrentUser = currentUserId === user.id;
          return (
            <TableRow key={user.id}>
              <TableCell className="font-mono text-sm">{user.username}</TableCell>
              <TableCell>{intl.formatMessage({ id: `userDialog.role.${user.role}` })}</TableCell>
              <TableCell>
                {user.tags && user.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {user.tags.map((tag) => (
                      <TagBadge key={tag.id} tag={tag} />
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-sm text-foreground">
                <FormattedDate
                  value={new Date(user.createdAt)}
                  year="numeric"
                  month="numeric"
                  day="numeric"
                  hour="2-digit"
                  minute="2-digit"
                  second="2-digit"
                />
              </TableCell>
              <TableCell className="text-sm text-foreground">
                <FormattedDate
                  value={new Date(user.updatedAt)}
                  year="numeric"
                  month="numeric"
                  day="numeric"
                  hour="2-digit"
                  minute="2-digit"
                  second="2-digit"
                />
              </TableCell>
              <TableCell>
                {isCurrentUser ? (
                  <div className="text-xs text-foreground text-right">—</div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(user)}
                      title={intl.formatMessage({ id: 'users.table.editUser' })}
                      data-testid="edit-user-button"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(user)}
                      title={intl.formatMessage({ id: 'users.table.deleteUser' })}
                      data-testid="delete-user-button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
